// Loader for EQUATOR checklist guidelines.
//
// The bundled guideline JSON files are validated authoritatively against
// `data/equator/_schema.json` at build time by `scripts/checklists-ci.mjs`
// (Ajv runs under Node where eval is fine). At runtime we cannot re-run Ajv:
// it compiles validators via `new Function(...)`, which MV3's strict CSP
// (`script-src 'self' 'wasm-unsafe-eval'`) refuses.
//
// Runtime therefore does a lightweight structural sanity check on each
// bundled file — enough to catch a catastrophic build-time corruption — plus
// the `verification_status` warning that gates v0.5 ship readiness (ADR-0002).

import consort from '../../../data/equator/consort-2010-v1.json';
import strobe from '../../../data/equator/strobe-v1.json';
import prisma from '../../../data/equator/prisma-2020-v1.json';
import stard from '../../../data/equator/stard-2015-v1.json';
import care from '../../../data/equator/care-v1.json';

import type { ChecklistGuideline } from './types';

interface GuidelineSource {
  filename: string;
  raw: unknown;
}

const SOURCES: GuidelineSource[] = [
  { filename: 'consort-2010-v1.json', raw: consort },
  { filename: 'strobe-v1.json', raw: strobe },
  { filename: 'prisma-2020-v1.json', raw: prisma },
  { filename: 'stard-2015-v1.json', raw: stard },
  { filename: 'care-v1.json', raw: care },
];

let cached: ChecklistGuideline[] | null = null;

function sanityCheck(source: GuidelineSource): ChecklistGuideline {
  const raw = source.raw;
  if (typeof raw !== 'object' || raw === null) {
    throw new Error(
      `EQUATOR checklist ${source.filename}: not an object at runtime (build-time CI should have caught this)`,
    );
  }
  const g = raw as Partial<ChecklistGuideline>;
  if (typeof g.guideline_id !== 'string' || g.guideline_id.length === 0) {
    throw new Error(`EQUATOR checklist ${source.filename}: missing guideline_id`);
  }
  if (typeof g.name !== 'string' || g.name.length === 0) {
    throw new Error(`EQUATOR checklist ${source.filename}: missing name`);
  }
  if (typeof g.version !== 'string' || g.version.length === 0) {
    throw new Error(`EQUATOR checklist ${source.filename}: missing version`);
  }
  if (!Array.isArray(g.applies_to) || g.applies_to.length === 0) {
    throw new Error(`EQUATOR checklist ${source.filename}: missing applies_to`);
  }
  if (!Array.isArray(g.sections) || g.sections.length === 0) {
    throw new Error(`EQUATOR checklist ${source.filename}: missing sections`);
  }
  const guideline = raw as ChecklistGuideline;
  if (
    guideline.verification_status !== 'verified' &&
    guideline.verification_status !== 'verified_with_consolidations'
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ManuscriptSkim] checklist ${guideline.guideline_id} v${guideline.version} carries verification_status="${guideline.verification_status}". See data/equator/_verification.md before v0.5 ship.`,
    );
  }
  return guideline;
}

/** Load and validate every bundled guideline. Cached after first call. */
export function loadAllGuidelines(): ChecklistGuideline[] {
  if (!cached) {
    cached = SOURCES.map(sanityCheck);
  }
  return cached;
}

/** Find a guideline by its stable id (e.g. "CONSORT-2010"). */
export function loadGuidelineById(id: string): ChecklistGuideline | undefined {
  return loadAllGuidelines().find((g) => g.guideline_id === id);
}

/**
 * Pick a guideline for a given lowercased study-type label (e.g. "randomised controlled trial").
 * Matches the first guideline whose `applies_to` includes the label. Used by the
 * Sprint-3 classifier to route the methodology audit to the right checklist.
 */
export function pickGuidelineForStudyType(studyType: string): ChecklistGuideline | undefined {
  const needle = studyType.toLowerCase().trim();
  return loadAllGuidelines().find((g) =>
    g.applies_to.some((label) => label.toLowerCase() === needle),
  );
}

/** Test helper. Not exported through the barrel. */
export function _resetForTests(): void {
  cached = null;
}
